/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    UPLOAD_CONDITION_VALIDATION
 * Date:         2025-01-02
 * Modified By:  Cursor
 *
 * Description:
 * Add condition validation during Excel/CSV upload. Invalid condition values
 * (like "Used – good", "Seller Refurbished") are flagged with conditionNeedsReview
 * to force user review before submit. Matches existing category validation pattern.
 *
 * File Size Check:
 * - Current: 171 lines
 * - Adding: ~5 lines
 * - Projected: 176 lines
 * - Decision: Proceed (within 200-line utility limit)
 *
 * Related Screens / Modules:
 * - client/src/features/broadcast/broadcast-create/useFileUpload.ts (will handle flag)
 * - client/src/features/broadcast/BroadcastItemRow.tsx (will show badge)
 * - client/src/services/broadcastService.ts (interface update)
 */
import * as XLSX from "xlsx";
import { CATEGORY_OPTIONS, CONDITION_OPTIONS } from "../constants/inventoryOptions";

export type ParsedItem = {
  partNumber: string; // from "SKU / Model / Part Number" (mandatory)
  manufacturer?: string;
  category?: string;
  quantity?: number | string;
  condition?: string;
  price?: number | string;
  description?: string;
  country?: string;
  categoryNeedsReview?: boolean; // Flag for invalid categories that need manual review
  conditionNeedsReview?: boolean; // [2025-01-02] Flag for invalid conditions that need manual review
  // keep room for other optional UI fields if they exist
};

export async function parseBroadcastFile(
  file: File,
): Promise<{ items: ParsedItem[]; errors: string[]; warnings: string[] }> {
  const errors: string[] = [];
  const warnings: string[] = [];
  const items: ParsedItem[] = [];

  try {
    // Read file as ArrayBuffer
    const arrayBuffer = await file.arrayBuffer();

    // Parse workbook
    const workbook = XLSX.read(arrayBuffer, { type: "array" });

    // Get first worksheet
    const sheetName = workbook.SheetNames[0];
    if (!sheetName) {
      errors.push("No worksheets found in file");
      return { items, errors, warnings };
    }

    const worksheet = workbook.Sheets[sheetName];

    // Convert to JSON array
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: "" });

    if (jsonData.length < 2) {
      errors.push("File must contain at least a header row and one data row");
      return { items, errors, warnings };
    }

    // Extract headers from first row
    const headers = (jsonData[0] as string[]).map((h) => normalizeHeader(h));

    // Find mandatory column using precedence rule
    const mandatoryColumnIndex = findMandatoryColumn(headers);

    if (mandatoryColumnIndex === -1) {
      errors.push(
        'Mandatory column "SKU / Model / Part Number" not found (or alias: partNumber/sku/model)',
      );
      return { items, errors, warnings };
    }

    // Process data rows (skip header row)
    for (let i = 1; i < jsonData.length; i++) {
      const row = jsonData[i] as string[];

      // Skip completely blank rows
      if (row.every((cell) => !cell || cell.toString().trim() === "")) {
        continue;
      }

      // Extract mandatory field
      const partNumber = row[mandatoryColumnIndex]?.toString().trim();

      if (!partNumber) {
        errors.push(`Row ${i + 1}: "SKU / Model / Part Number" is required`);
        continue;
      }

      // Create parsed item
      const item: ParsedItem = {
        partNumber,
      };

      // Map optional columns
      headers.forEach((header, index) => {
        if (index === mandatoryColumnIndex) return; // Skip mandatory column

        const value = row[index]?.toString().trim();
        if (!value) return;

        switch (header) {
          case "manufacturer":
            item.manufacturer = value;
            break;
          case "category":
          case "categories":
            // Support comma-separated categories or single category
            const categoryValues = value.split(",").map((c) => c.trim()).filter((c) => c);
            if (categoryValues.length > 0) {
              // For now, store first category for backward compatibility
              // Multiple categories will be handled in the form
              item.category = categoryValues[0];
              // Check if any category is invalid
              const hasInvalid = categoryValues.some((cat) => !CATEGORY_OPTIONS.includes(cat as any));
              if (hasInvalid) {
                item.categoryNeedsReview = true;
              }
            }
            break;
          case "quantity":
            item.quantity = isNaN(Number(value)) ? value : Number(value);
            break;
          case "condition":
            item.condition = value;
            // [2025-01-02] Validate condition against valid options (matching category pattern)
            if (!CONDITION_OPTIONS.includes(value as any)) {
              item.conditionNeedsReview = true; // Flag for manual review in form
            }
            break;
          case "price":
            item.price = isNaN(Number(value)) ? value : Number(value);
            break;
          case "description":
            item.description = value;
            break;
          case "country":
            item.country = value;
            break;
          default:
            // Ignore unknown columns
            break;
        }
      });

      // Soft validations (warnings) so user can fix in UI after upload.
      const rowNo = i + 1;
      const mfr = typeof item.manufacturer === "string" ? item.manufacturer.trim() : "";
      if (!mfr) warnings.push(`Row ${rowNo}: Manufacturer is required`);
      else if (mfr.length < 2) warnings.push(`Row ${rowNo}: Manufacturer must be at least 2 characters`);
      else if (mfr.length > 80) warnings.push(`Row ${rowNo}: Manufacturer must be 80 characters or less`);

      const cond = typeof item.condition === "string" ? item.condition.trim() : "";
      if (!cond) warnings.push(`Row ${rowNo}: Condition is required`);
      else if (!CONDITION_OPTIONS.includes(cond as any)) {
        warnings.push(`Row ${rowNo}: Condition must be one of: ${CONDITION_OPTIONS.join(", ")}`);
      }

      const qtyRaw = item.quantity;
      const qtyNum = typeof qtyRaw === "number" ? qtyRaw : qtyRaw ? Number(qtyRaw) : NaN;
      if (!qtyRaw || !Number.isFinite(qtyNum)) {
        warnings.push(`Row ${rowNo}: Quantity is required`);
      } else if (!Number.isInteger(qtyNum)) {
        warnings.push(`Row ${rowNo}: Quantity must be a whole number`);
      } else if (qtyNum < 1) {
        warnings.push(`Row ${rowNo}: Quantity must be at least 1`);
      }

      const desc = typeof item.description === "string" ? item.description : "";
      if (desc && desc.length > 50) {
        warnings.push(`Row ${rowNo}: Description must be 50 characters or less`);
      }

      items.push(item);
    }
  } catch (error) {
    errors.push(
      `Failed to parse file: ${error instanceof Error ? error.message : "Unknown error"}`,
    );
  }

  return { items, errors, warnings };
}

function normalizeHeader(header: string): string {
  return header
    .toLowerCase()
    .trim()
    .replace(/[/\s_]+/g, "") // Replace slashes, spaces, underscores with empty string
    .replace(/\s+/g, ""); // Remove any remaining spaces
}

function findMandatoryColumn(headers: string[]): number {
  // Precedence rule: combined column > partnumber > sku > model
  const combinedPatterns = ["skumodelpartnumber", "skupartnumber", "modelpartnumber", "skumodel"];

  const fallbackPatterns = ["partnumber", "part_number", "sku", "model"];

  // Check for combined column patterns first
  for (let i = 0; i < headers.length; i++) {
    const header = headers[i];
    if (combinedPatterns.some((pattern) => header.includes(pattern))) {
      return i;
    }
  }

  // Check for fallback patterns
  for (let i = 0; i < headers.length; i++) {
    const header = headers[i];
    if (fallbackPatterns.some((pattern) => header === pattern)) {
      return i;
    }
  }

  return -1; // Not found
}
